import PageHero from '../components/PageHero.jsx';
import KeyFacts from '../components/KeyFacts.jsx';
import SafetyNotice from '../components/SafetyNotice.jsx';
import ResourceList from '../components/ResourceList.jsx';
import PlatformFlow from '../components/PlatformFlow.jsx';
import { FadeUp } from '../components/Motion.jsx';
import { IconVial, IconPerson } from '../components/Icons.jsx';

const EMA_URL = 'https://www.ema.europa.eu/en/medicines/human/EPAR/foscan#product-information';

const keyFacts = [
  { label: 'Treatment type', value: 'Light-activated therapy' },
  { label: 'Requires', value: 'Photosensitiser, laser light and oxygen' },
  { label: 'Early-stage aim', value: 'Complete removal of the cancer' },
  { label: 'Advanced disease aim', value: 'Shrink the cancer to alleviate symptoms' },
  { label: 'After prior therapy', value: 'Can be used safely following surgery, radiotherapy or chemotherapy' },
  { label: 'Healing', value: 'New, normal cells replace treated cells' },
];

const related = [
  { label: 'Head & neck cancer', meta: 'Clinical background', to: '/head-neck-cancer', Icon: IconPerson },
  { label: 'Adverse event report', meta: 'Pharmacovigilance', to: '/adverse-event-report', Icon: IconVial },
];

const sideEffects = [
  'Heightened sensitivity to light following administration of the photosensitising agent',
  'Pain, which can be controlled with painkillers',
  'Swelling',
  'Bleeding',
  'Difficulty swallowing',
];

export default function PDT() {
  return (
    <>
      <PageHero
        eyebrow="TREATMENT • PHOTODYNAMIC THERAPY"
        title="Photodynamic therapy"
        subtitle="A light-activated treatment combining a photosensitiser, laser light and oxygen."
      />

      <div className="page-shell">
        <div className="page-grid">
          <div className="page-main">
            <FadeUp>
              <section className="page-section" id="what-is">
                <h2 className="page-section-title">What is photodynamic therapy?</h2>
                <p className="page-body">
                  <strong>Photodynamic therapy (PDT)</strong> is a medical treatment for
                  several types of cancer and disease. Photodynamic therapy uses a
                  combination of laser light of a specific wavelength, oxygen and a
                  light-sensitive drug to destroy cancer cells.
                </p>
              </section>
            </FadeUp>

            <FadeUp>
              <section className="page-section" id="objectives">
                <h2 className="page-section-title">Treatment objectives</h2>
                <p className="page-body">
                  In early-stage disease, the aim of treatment with photodynamic therapy
                  may be to completely remove and cure the cancer. In advanced disease,
                  however, the goal may be to shrink the cancer in order to alleviate
                  symptoms. In this scenario, photodynamic therapy cannot cure the cancer.
                </p>
                <p className="page-body">
                  Even patients who have had surgery, radiotherapy or chemotherapy in the
                  past can be treated safely with photodynamic therapy.
                </p>
              </section>
            </FadeUp>

            <FadeUp>
              <section className="page-section" id="side-effects">
                <h2 className="page-section-title">Possible side effects</h2>
                <p className="page-body">
                  As with all treatments, patients may experience side effects following
                  treatment with photodynamic therapy. Temporary side effects may include:
                </p>
                <ul className="bullet-list">
                  {sideEffects.map((s) => (
                    <li key={s}>{s}</li>
                  ))}
                </ul>
              </section>
            </FadeUp>

            <SafetyNotice
              title="Light exposure precautions"
              linkLabel="View complete product information"
              linkHref={EMA_URL}
            >
              <p>
                Following administration of the photosensitising agent, patients become
                highly sensitive to light. Precautions must be taken to avoid exposure of
                skin and eyes to direct sunlight or bright indoor lighting for a specified
                period following injection. Appropriate clothing and eyewear must be worn
                to prevent photosensitivity reactions.
              </p>
            </SafetyNotice>

            <FadeUp>
              <section className="page-section" id="healing">
                <h2 className="page-section-title">Healing after treatment</h2>
                <p className="page-body">
                  New, normal cells replace those cells killed by photodynamic therapy
                  allowing rapid healing to occur post-treatment. This avoids the scarring
                  disfigurement that may occur with other types of tissue removal.
                </p>
              </section>
            </FadeUp>
          </div>

          <div className="page-aside">
            <KeyFacts items={keyFacts} />
            <ResourceList title="Related information" items={related} />
          </div>
        </div>

        <FadeUp>
          <section className="page-band" id="platform">
            <div className="page-band-head">
              <span className="pill-badge">THE COMPLETE PLATFORM</span>
              <h2 className="page-section-title">One integrated system</h2>
              <p className="page-body">
                Photosensitizers work alongside purpose-built lasers and optical fibres as
                part of a photodynamic therapy platform, designed so the photosensitiser,
                light source and delivery system work together as one integrated solution.
              </p>
            </div>
            <PlatformFlow />
          </section>
        </FadeUp>
      </div>
    </>
  );
}